import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isUserOnline, getLastSeenText } from './useUserActivity';

interface ChildOnlineStatus {
  userId: string;
  isOnline: boolean;
  lastSeen: string;
  showOnlineStatus: boolean;
}

export const useChildOnlineStatus = (childIds: string[]) => {
  const [statuses, setStatuses] = useState<Record<string, ChildOnlineStatus>>({});
  const [loading, setLoading] = useState(true);

  const fetchStatuses = useCallback(async () => {
    if (childIds.length === 0) {
      setStatuses({});
      setLoading(false);
      return;
    }

    try {
      // Fetch activity data for all children
      const { data: activityData, error: activityError } = await supabase
        .from('user_activity')
        .select('user_id, last_active_at')
        .in('user_id', childIds);

      if (activityError) {
        console.error('Error fetching child activity:', activityError);
      }

      // Fetch privacy settings for all children
      const { data: privacyData, error: privacyError } = await supabase
        .from('user_privacy_settings')
        .select('user_id, show_online_status')
        .in('user_id', childIds);

      if (privacyError) {
        console.error('Error fetching privacy settings:', privacyError);
      }

      const newStatuses: Record<string, ChildOnlineStatus> = {};
      
      for (const childId of childIds) {
        const activity = activityData?.find(a => a.user_id === childId);
        const privacy = privacyData?.find(p => p.user_id === childId);
        
        const showOnlineStatus = privacy?.show_online_status ?? false;
        const lastActiveAt = activity?.last_active_at || null;

        newStatuses[childId] = {
          userId: childId,
          isOnline: showOnlineStatus && isUserOnline(lastActiveAt),
          lastSeen: showOnlineStatus ? getLastSeenText(lastActiveAt) : 'Hidden',
          showOnlineStatus
        };
      }

      setStatuses(newStatuses);
    } catch (err) {
      console.error('Failed to fetch child statuses:', err);
    } finally {
      setLoading(false);
    }
  }, [childIds]);

  useEffect(() => {
    fetchStatuses();

    // Refresh every 30 seconds
    const interval = setInterval(fetchStatuses, 30000);

    return () => clearInterval(interval);
  }, [fetchStatuses]);

  return { statuses, loading, refreshStatuses: fetchStatuses };
};
